import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { MyNotificationApi } from '../tokens/my-notifications.token';
import { MyNotification, NotificationPage, NotificationQuery } from '../models/notification.model';

/**
 * REST-only implementation of `MyNotificationApi` (EM-310).
 *
 * Calls:
 *   GET  /services/embedded/api/me/notifications?status&limit&cursor
 *   GET  /services/embedded/api/me/notifications/unread-count   (cheap badge poll)
 *   POST /services/embedded/api/me/notifications/{id}/ack
 *   POST /services/embedded/api/me/notifications/mark-all-read
 *
 * Rides the existing Keycloak bearer (attached by `authInterceptor`). The endpoint is
 * per-principal server-side (SME → party; staff → their login + roles). The unread count
 * is held in ONE signal and is the single source for the bell badge / dashboard / inbox
 * (Rule 17). WebSocket push is future (§6.1) — the count is synthesised from the list
 * response (Rule-10 preferred pattern; no Mock).
 *
 * Error surfacing (Rule 09) is the caller's responsibility via
 * `extractBackendErrorMessage` — this service propagates the raw `HttpErrorResponse`.
 */
@Injectable()
export class MyNotificationRestService implements MyNotificationApi {
  private readonly http = inject(HttpClient);
  private readonly base = API_PATHS.ME_NOTIFICATIONS;

  private readonly unreadSig = signal<number>(0);
  private readonly itemsSig = signal<MyNotification[]>([]);
  private readonly cursorSig = signal<string | null>(null);
  private readonly endSig = signal<boolean>(false);

  readonly unreadCount = this.unreadSig.asReadonly();
  readonly items = this.itemsSig.asReadonly();
  readonly endReached = computed(() => this.endSig());

  load(query?: NotificationQuery): Observable<NotificationPage> {
    return this.http.get<NotificationPage>(this.base, { params: this.buildParams(query) }).pipe(
      tap((page) => {
        this.itemsSig.set(page.items ?? []);
        this.unreadSig.set(page.unreadCount ?? 0);
        this.cursorSig.set(page.nextCursor ?? null);
        this.endSig.set(!page.nextCursor);
      }),
    );
  }

  loadMore(query?: Pick<NotificationQuery, 'status'>): Observable<NotificationPage> {
    const cursor = this.cursorSig();
    return this.http
      .get<NotificationPage>(this.base, { params: this.buildParams({ ...query, cursor }) })
      .pipe(
        tap((page) => {
          this.itemsSig.update((existing) => [...existing, ...(page.items ?? [])]);
          this.unreadSig.set(page.unreadCount ?? this.unreadSig());
          this.cursorSig.set(page.nextCursor ?? null);
          this.endSig.set(!page.nextCursor);
        }),
      );
  }

  refreshUnreadCount(): Observable<number> {
    // Cheap count-only endpoint — a server-side COUNT in the per-principal scope, not a full inbox
    // load. Safe to call on the 60s bell poll without materialising/grouping every notification.
    return new Observable<number>((subscriber) => {
      this.http.get<{ unread: number }>(`${this.base}/unread-count`).subscribe({
        next: (res) => {
          const count = res?.unread ?? 0;
          this.unreadSig.set(count);
          subscriber.next(count);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });
    });
  }

  ack(notificationId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/${notificationId}/ack`, {}).pipe(
      tap(() => {
        // Flip the item locally and decrement the badge — no full refetch needed.
        this.itemsSig.update((items) =>
          items.map((n) => (n.notificationId === notificationId ? { ...n, status: 'read' as const } : n)),
        );
        this.unreadSig.update((c) => Math.max(0, c - 1));
      }),
    );
  }

  markAllRead(): Observable<number> {
    return new Observable<number>((subscriber) => {
      this.http.post<{ updated: number }>(`${this.base}/mark-all-read`, {}).subscribe({
        next: (res) => {
          this.itemsSig.update((items) => items.map((n) => ({ ...n, status: 'read' as const })));
          this.unreadSig.set(0);
          subscriber.next(res?.updated ?? 0);
          subscriber.complete();
        },
        error: (err) => subscriber.error(err),
      });
    });
  }

  private buildParams(query?: NotificationQuery): HttpParams {
    let params = new HttpParams();
    if (query?.status) {
      params = params.set('status', query.status);
    }
    if (query?.limit != null) {
      params = params.set('limit', String(query.limit));
    }
    if (query?.cursor) {
      params = params.set('cursor', query.cursor);
    }
    return params;
  }
}
